/**
 * 24SEVEN — "What's the difference" paddle comparison.
 *
 * Each column's paddle can be swapped from a dropdown on its name. Picking a
 * paddle that is already showing in another visible column swaps the two, so
 * the table never shows the same paddle twice. Column content comes from
 * server-rendered <template data-pl-model> elements, so prices, images and
 * ratings are always Shopify's own.
 */

class PlCompare extends HTMLElement {
  #openCol = null;
  #activeIndex = -1;
  #mq = window.matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocClick);
    this.#mq.addEventListener?.('change', this.#onLayoutChange);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick);
    this.#mq.removeEventListener?.('change', this.#onLayoutChange);
  }

  /** Columns visible at the current width (2 on phones/tablets, up to 3 on desktop). */
  get #visibleCols() {
    const count = Number(this.dataset.count) || 0;
    return Math.min(count, this.#mq.matches ? 3 : 2);
  }

  #head(col) {
    return this.querySelector(`.pl-cell--head[data-col="${col}"]`);
  }

  #modelIn(col) {
    return this.#head(col)?.dataset.model;
  }

  /* ---- Rendering ------------------------------------------------------- */

  #setColumn(col, modelId) {
    const tpl = this.querySelector(`template[data-pl-model="${CSS.escape(modelId)}"]`);
    const head = this.#head(col);
    if (!tpl || !head) return;

    head.dataset.model = modelId;
    for (const part of tpl.content.querySelectorAll('[data-part]')) {
      const name = part.dataset.part;
      const target =
        name === 'media' || name === 'info'
          ? head.querySelector(`:scope > [data-part="${name}"]`)
          : this.querySelector(`.pl-cell[data-col="${col}"][data-part="${name}"]`);
      if (target) target.innerHTML = part.innerHTML;
    }

    const list = head.querySelector('.pl-pick__list');
    let label = modelId;
    for (const opt of list.querySelectorAll('[role="option"]')) {
      const selected = opt.dataset.model === modelId;
      opt.setAttribute('aria-selected', String(selected));
      if (selected) label = opt.dataset.name;
    }
    head.querySelector('[data-pl-name]').textContent = label;
    head.querySelector('[data-pl-pick]').setAttribute('aria-label', `Change paddle in column ${col}. Currently ${label}`);
  }

  #choose(col, modelId) {
    const current = this.#modelIn(col);
    if (!modelId || modelId === current) return;

    // If that paddle is already on screen, trade places with it.
    for (let other = 1; other <= this.#visibleCols; other++) {
      if (other !== col && this.#modelIn(other) === modelId) {
        this.#setColumn(other, current);
        break;
      }
    }
    // A hidden third column (phones) could also hold it; keep the set unique there too.
    const count = Number(this.dataset.count) || 0;
    for (let other = this.#visibleCols + 1; other <= count; other++) {
      if (this.#modelIn(other) === modelId) this.#setColumn(other, current);
    }

    this.#setColumn(col, modelId);
  }

  /** Going from desktop to mobile can hide a column; nothing to re-render, but close any open list. */
  #onLayoutChange = () => this.#close();

  /* ---- Dropdown -------------------------------------------------------- */

  #options(col) {
    return [...this.#head(col).querySelectorAll('[role="option"]')];
  }

  #open(col) {
    this.#close();
    const head = this.#head(col);
    const btn = head.querySelector('[data-pl-pick]');
    const list = head.querySelector('.pl-pick__list');
    list.hidden = false;
    btn.setAttribute('aria-expanded', 'true');
    this.#openCol = col;
    const opts = this.#options(col);
    this.#setActive(Math.max(0, opts.findIndex((o) => o.getAttribute('aria-selected') === 'true')));
    list.focus({ preventScroll: true });
  }

  #close({ focusButton = false } = {}) {
    if (this.#openCol == null) return;
    const head = this.#head(this.#openCol);
    const btn = head.querySelector('[data-pl-pick]');
    const list = head.querySelector('.pl-pick__list');
    list.hidden = true;
    list.removeAttribute('aria-activedescendant');
    btn.setAttribute('aria-expanded', 'false');
    for (const o of this.#options(this.#openCol)) o.removeAttribute('data-active');
    this.#openCol = null;
    this.#activeIndex = -1;
    if (focusButton) btn.focus();
  }

  #setActive(index) {
    const opts = this.#options(this.#openCol);
    if (!opts.length) return;
    this.#activeIndex = (index + opts.length) % opts.length;
    opts.forEach((o, i) => o.toggleAttribute('data-active', i === this.#activeIndex));
    const active = opts[this.#activeIndex];
    active.closest('.pl-pick__list').setAttribute('aria-activedescendant', active.id);
    active.scrollIntoView({ block: 'nearest' });
  }

  #onClick = (event) => {
    const opt = event.target.closest('[role="option"]');
    if (opt && this.contains(opt)) {
      const col = Number(opt.closest('.pl-cell--head').dataset.col);
      this.#close({ focusButton: true });
      this.#choose(col, opt.dataset.model);
      return;
    }
    const btn = event.target.closest('[data-pl-pick]');
    if (btn) {
      const col = Number(btn.closest('.pl-cell--head').dataset.col);
      if (this.#openCol === col) this.#close();
      else this.#open(col);
    }
  };

  #onDocClick = (event) => {
    if (this.#openCol == null) return;
    if (!this.#head(this.#openCol).querySelector('.pl-pick').contains(event.target)) this.#close();
  };

  #onKeydown = (event) => {
    const btn = event.target.closest?.('[data-pl-pick]');
    if (btn && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      event.preventDefault();
      this.#open(Number(btn.closest('.pl-cell--head').dataset.col));
      return;
    }
    if (this.#openCol == null) return;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#setActive(this.#activeIndex + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#setActive(this.#activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        this.#setActive(0);
        break;
      case 'End':
        event.preventDefault();
        this.#setActive(-1);
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const col = this.#openCol;
        const opt = this.#options(col)[this.#activeIndex];
        this.#close({ focusButton: true });
        if (opt) this.#choose(col, opt.dataset.model);
        break;
      }
      case 'Escape':
        event.preventDefault();
        this.#close({ focusButton: true });
        break;
      case 'Tab':
        this.#close();
        break;
    }
  };
}

if (!customElements.get('pl-compare')) customElements.define('pl-compare', PlCompare);
